import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, Search, Plus, ShieldAlert } from "lucide-react";
import { Navbar, Footer } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ComplaintCard } from "@/components/ComplaintCard";
import { ComplaintForm } from "@/components/ComplaintForm";
import api from "@/lib/api";

const FILTERS = [
  { value: "", label: "Todas" },
  { value: "aberta", label: "Aguardando resposta" },
  { value: "respondida", label: "Respondidas" },
  { value: "resolvida", label: "Resolvidas" },
];

export default function ReclameAqui() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ complaints: [], totals: { total: 0, abertas: 0, respondidas: 0, resolvidas: 0 } });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    api.get(`/complaints?${params}`).then((r) => setData(r.data)).catch((err) => console.error("Falha ao carregar reclamações:", err)).finally(() => setLoading(false));
  }, [q, status]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const openForm = () => (user ? setFormOpen(true) : navigate("/login"));

  return (
    <div className="min-h-screen bg-[#fafaf8]">
      <Navbar />

      <section className="bg-[#1b5e3f] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#f59e0b] bg-white/10 px-3 py-1.5 rounded-full">
            <ShieldAlert className="w-3.5 h-3.5" /> Reclame Aqui
          </span>
          <h1 className="font-head text-4xl sm:text-5xl font-bold text-white mt-5">Sua voz tem poder</h1>
          <p className="text-white/80 mt-3 max-w-2xl">Teve um problema com uma empresa? Registre a sua reclamação publicamente, anexe provas e acompanhe a resposta. As empresas são notificadas por e-mail.</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Button data-testid="reclame-new-btn" onClick={openForm} className="rounded-xl bg-[#f59e0b] hover:bg-[#d97706]">
              <Plus className="w-4 h-4 mr-2" /> Fazer reclamação
            </Button>
          </div>
          <div className="flex flex-wrap gap-6 mt-8 text-white/90 text-sm">
            <span data-testid="reclame-total"><strong className="font-head text-2xl text-white">{data.totals.total}</strong> reclamações</span>
            <span data-testid="reclame-respondidas"><strong className="font-head text-2xl text-white">{data.totals.respondidas}</strong> respondidas</span>
            <span data-testid="reclame-resolvidas"><strong className="font-head text-2xl text-white">{data.totals.resolvidas}</strong> resolvidas</span>
          </div>
        </div>
        <div className="absolute -right-24 -bottom-24 w-80 h-80 rounded-full bg-[#13422c]" />
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716c]" />
            <input data-testid="reclame-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar empresa..." className="w-full bg-white border border-[#e7e5e4] rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b5e3f]/20 focus:border-[#1b5e3f] transition-all" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {FILTERS.map((f) => (
              <button key={f.value} data-testid={`reclame-filter-${f.value || "todas"}`} onClick={() => setStatus(f.value)} className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${status === f.value ? "bg-[#1b5e3f] text-white border-[#1b5e3f]" : "bg-white text-[#57534e] border-[#e7e5e4] hover:border-[#1b5e3f]"}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-24 rounded-2xl bg-[#e7e5e4]/40 animate-pulse" />)}</div>
        ) : data.complaints.length === 0 ? (
          <div data-testid="reclame-empty" className="bg-white rounded-2xl border border-[#e7e5e4] p-12 text-center">
            <Megaphone className="w-10 h-10 text-[#1b5e3f]/30 mx-auto mb-3" />
            <p className="text-[#78716c]">Nenhuma reclamação encontrada.</p>
            <Button onClick={openForm} variant="outline" className="mt-4 rounded-xl border-[#1b5e3f] text-[#1b5e3f]">Seja o primeiro a reclamar</Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="reclame-list">
            {data.complaints.map((c) => (
              <ComplaintCard key={c.id} complaint={c} user={user} onChanged={load} />
            ))}
          </div>
        )}
      </section>

      <ComplaintForm open={formOpen} onClose={() => setFormOpen(false)} onCreated={load} />
      <Footer />
    </div>
  );
}
